import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityPermission, IdentityPermissionName } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save permission data.
 */
@Injectable()
export class IdentityPermissionService {
    public static INCLUDE_USER_W_PERSON: string = 'user.person';

    private readonly identityApiBaseUrl: string;
    private cachedPermissionsMap: { [apiRequest: string]: IdentityPermission[]; } = {};

    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all permissions
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityPermission[]> {
        queryParams = queryParams || [];
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh &&
            this.cachedPermissionsMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedPermissionsMap[apiRequestUrl]
                .map((permission: IdentityPermission) => permission.clone()));
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const permissions: IdentityPermission[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const permission of bodyData) {
                    permissions.push((new IdentityPermission()).deserialize(permission, included));
                }

                this.cachedPermissionsMap[apiRequestUrl] = permissions;
                return permissions.map((permission: IdentityPermission) => permission.clone());
            }));
    }

    /**
     * Get permissions along with their related resource name (e.g team name, organization name, etc)
     */
    public getResourceNames(queryParams: string[] = null): Observable<IdentityPermissionName[]> {
        queryParams = queryParams || [];
        const apiRequestUrl: string = this.getApiUrl('resource-names', null, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const permissions: IdentityPermissionName[] = [];

                const bodyData: any = response.data || [];
                for (const permission of bodyData) {
                    permissions.push((new IdentityPermissionName()).deserialize(permission));
                }

                return permissions;
            }));
    }

    /**
     * Add or update the permission
     */
    public save(permission: IdentityPermission, includes: string[] = null): Observable<IdentityPermission> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(permission.id, includes);

        if (permission.id) {
            // Update existing
            response = this.http.patch(apiRequestUrl, permission.serialize());
        } else {
            // Add a new
            response = this.http.post(apiRequestUrl, permission.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityPermission()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified permission.
     */
    public delete(identityPermissionId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityPermissionId);
        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(permissionId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/permissions/`;

        if (permissionId) {
            url += permissionId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedPermissionsMap = {};
    }
}
